import { DateTime } from 'luxon';
import { LuxonDateTimeFormats, YardTaskCheckEntity, YardTaskFlags } from '@nova/core';
import { isEmpty } from 'lodash';

import type {
  ChecklistItem,
  CheckTaskApiRequestData,
  CheckTaskApiResponseData,
  CheckTaskCreateData,
  CheckTaskEditData,
  MoveTaskApiRequestData,
  MoveTaskApiResponseData,
  MoveTaskCreateData,
  MoveTaskEditData,
  TimeInput
} from '@nova/core';

type TaskData =
  | CheckTaskCreateData
  | CheckTaskEditData
  | CheckTaskApiResponseData
  | MoveTaskCreateData
  | MoveTaskEditData
  | MoveTaskApiResponseData;

export function isCheckTask<T extends TaskData>(taskData: T): taskData is T & { type: 'check' } {
  return taskData.type === 'check';
}

export function isMoveTask<T extends TaskData>(taskData: T): taskData is T & { type: 'move' } {
  return taskData.type === 'move';
}

/**
 * Builds the due date time by combining date and time
 * @param dueByDate - ISO date string
 * @param dueByTime - Time
 * @param warehouseTimezone - Warehouse timezone
 * @returns ISO date string with time, or null if no date provided
 */
export function mapDueDateTime(
  dueByDate?: string,
  dueByTime?: TimeInput,
  warehouseTimezone?: string
): string | null {
  if (!dueByDate) {
    return null;
  }

  const newDateTimeString = [
    DateTime.fromISO(dueByDate).toFormat('yyyy-MM-dd'),
    dueByTime?.time || '11:59',
    dueByTime?.amPm || 'PM'
  ].join(' ');

  const newDateTime = DateTime.fromFormat(
    newDateTimeString,
    `${LuxonDateTimeFormats.DateDashed} ${LuxonDateTimeFormats.Extended12HrTimePadding} a`
  ).setZone(warehouseTimezone, { keepLocalTime: true });

  return newDateTime?.isValid ? newDateTime.toISO() : null;
}

/**
 * Maps checklist items from form format to API format.
 * The mapper assumes that the checklist is an array of objects with a text and complete property.
 * If the complete property is not present, it defaults to false.
 * @param checklist - Form checklist items
 * @returns API checklist format
 */
export function mapChecklist(checklist?: ChecklistItem[]): ChecklistItem[] {
  if (!Array.isArray(checklist)) {
    return [];
  }

  if (checklist.length === 0) {
    return [];
  }

  /**
   * At this point, the checklist has been validated, so the reduce is just to
   * remove a possibly trailing empty item
   */
  return checklist.reduce((acc, item) => {
    if (isEmpty(item.description)) {
      return acc;
    }

    return [
      ...acc,
      {
        complete: item.complete || false,
        description: item.description
      }
    ];
  }, [] as ChecklistItem[]);
}

/**
 * Maps related entity and ID based on subject type
 * @param subject - Subject type
 * @param spot - Spot ID
 * @param area - Area ID
 * @param asset - Asset ID
 * @returns Object with relatedEntity and relatedId
 */
export function mapRelatedEntity(subject: string, spot?: string, area?: string, asset?: string) {
  const relatedEntity = subject;
  let relatedId: string | null = null;

  if (relatedEntity === YardTaskCheckEntity.Spot) {
    relatedId = spot || null;
  } else if (relatedEntity === YardTaskCheckEntity.SpotArea) {
    relatedId = area || null;
  } else {
    relatedId = asset || null;
  }

  return { relatedEntity, relatedId };
}

/**
 * Maps urgency flag to API format
 * @param isUrgent - Whether task is urgent
 * @returns Array of tags
 */
export function mapTags(isUrgent?: boolean): string[] {
  const tags: string[] = [];
  if (isUrgent) {
    tags.push(YardTaskFlags.URGENT);
  }
  return tags;
}

function transformCheckTaskCreateDataToApiRequestData(
  taskData: CheckTaskCreateData,
  warehouseId: string,
  warehouseTimezone: string
): CheckTaskApiRequestData | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  const checklist = mapChecklist(taskData.checklist);
  const { relatedEntity, relatedId } = mapRelatedEntity(
    taskData.subject,
    taskData.spot,
    taskData.area,
    taskData.asset
  );

  const tags = mapTags(taskData.isUrgent);
  const dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);

  return {
    warehouseId,
    type: 'check',
    assignedTo: taskData.assignee || null,
    description: taskData.description || '',
    child: {
      checklist,
      category: taskData.category,
      relatedEntity,
      relatedId
    },
    dueDateTime,
    tags
  };
}

function transformMoveTaskCreateDataToApiRequestData(
  taskData: MoveTaskCreateData,
  warehouseId: string,
  warehouseTimezone: string
): MoveTaskApiRequestData | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  const tags = mapTags(taskData.isUrgent);
  const dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);

  return {
    warehouseId,
    type: 'move',
    assignedTo: taskData.assignee || null,
    description: taskData.description || '',
    child: {
      ...(taskData.asset && { assetContainerId: taskData.asset }),
      ...(taskData.originSpot && { originSpotId: taskData.originSpot }),
      ...(taskData.destinationSpot && { destinationSpotId: taskData.destinationSpot })
    },
    dueDateTime,
    tags
  };
}

/**
 * Transforms form state to API payload format
 * @param taskData - Form task data
 * @param warehouseId - Warehouse ID
 * @param warehouseTimezone - Warehouse timezone
 * @returns API payload or null if invalid
 */
export function transformCreateDataToApiRequestData(
  taskData: CheckTaskCreateData | MoveTaskCreateData | undefined,
  warehouseId: string,
  warehouseTimezone: string
): CheckTaskApiRequestData | MoveTaskApiRequestData | null {
  if (!taskData) {
    console.error('No task data available');
    return null;
  }

  if (isCheckTask(taskData)) {
    return transformCheckTaskCreateDataToApiRequestData(taskData, warehouseId, warehouseTimezone);
  }

  if (isMoveTask(taskData)) {
    return transformMoveTaskCreateDataToApiRequestData(taskData, warehouseId, warehouseTimezone);
  }

  return null;
}
